import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Target, TrendingUp, Settings } from 'lucide-react';
import { MultiThresholdOptimizer } from './MultiThresholdOptimizer';
import { SequenceSensitivityAnalyzer } from './SequenceSensitivityAnalyzer';
import { PerformanceHeatmap } from './PerformanceHeatmap';
import { OptimizationRecommendations } from './OptimizationRecommendations';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode, AnalysisConfig } from '@/types/trading-analysis-types';

interface OptimizationTabProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
  analysisConfig: AnalysisConfig;
}

export const OptimizationTab: React.FC<OptimizationTabProps> = ({
  selectedFile,
  selectedAnalysisMode,
  analysisConfig
}) => {
  const [activeOptimizationTab, setActiveOptimizationTab] = useState('multi-threshold');

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file from the "View Results" tab to run optimizations
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Auto Optimization</h3>
          <p className="text-muted-foreground">
            Automatically find optimal parameters for maximum accuracy
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">File: {selectedFile.filename}</Badge>
          <Badge variant="outline">Mode: {selectedAnalysisMode}</Badge>
        </div>
      </div>

      {/* Phase 6 Features Overview */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            Phase 6: Advanced Optimization Features
          </CardTitle>
          <CardDescription>
            Professional-grade optimization tools to maximize your trading strategy performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Multi-Threshold Testing
              </h4>
              <p className="text-sm text-muted-foreground">
                Test multiple win thresholds simultaneously to find the optimal balance between accuracy and prediction frequency.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Sequence Sensitivity Analysis
              </h4>
              <p className="text-sm text-muted-foreground">
                Analyze how sequence length affects prediction performance to identify the "sweet spot" for your data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeOptimizationTab} onValueChange={setActiveOptimizationTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="multi-threshold" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Multi-Threshold
          </TabsTrigger>
          <TabsTrigger value="sequence-sensitivity" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Sequence Sensitivity
          </TabsTrigger>
          <TabsTrigger value="performance-heatmap" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Performance Heatmap
          </TabsTrigger>
          <TabsTrigger value="ai-recommendations" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            AI Recommendations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="multi-threshold" className="space-y-6">
          <MultiThresholdOptimizer
            selectedFile={selectedFile}
            selectedAnalysisMode={selectedAnalysisMode}
            maxSequenceLength={analysisConfig.maxPatternLength || 8}
          />
        </TabsContent>

        <TabsContent value="sequence-sensitivity" className="space-y-6">
          <SequenceSensitivityAnalyzer
            selectedFile={selectedFile}
            selectedAnalysisMode={selectedAnalysisMode}
            winThreshold={0.52} // Default threshold, could be made configurable
          />
        </TabsContent>

        <TabsContent value="performance-heatmap" className="space-y-6">
          <PerformanceHeatmap
            selectedFile={selectedFile}
            selectedAnalysisMode={selectedAnalysisMode}
          />
        </TabsContent>

        <TabsContent value="ai-recommendations" className="space-y-6">
          <OptimizationRecommendations
            selectedFile={selectedFile}
            selectedAnalysisMode={selectedAnalysisMode}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};